import type _algosdk from 'algosdk'
import { PROVIDER_ID } from '../constants'
import type { WalletProvider, Asset, Wallet, AccountInfo } from '../../types/wallet'
import { ConfirmedTxn, TxnType } from 'types/node'
import { TransactionsArray, TxnInfo } from 'types/api'

export type InitAlgodClient = {
  algosdk: typeof _algosdk
  token: string
  server: string
  port: string
}

export interface BaseWalletInterface {
  connect(onDisconnect: () => void): Promise<Wallet>
  healthCheck(): Promise<Record<string, never>>
  disconnect(): Promise<void>
  reconnect(onDisconnect: () => void): Promise<Wallet | null>
  decodeTransaction(txn: string, isSigned: boolean): _algosdk.Transaction
  logEncodedTransaction(txn: string, isSigned: boolean): void
  groupTransactionsBySender(transactions: TransactionsArray): Record<string, TxnInfo[]>
  /**
   * @param transactions array of base64 encoded unsigned transactions
   * @returns array of raw signed transactions
   */
  signTransactions(transaction: string[]): Promise<Uint8Array[]>
  signEncodedTransactions(transactions: TransactionsArray): Promise<Uint8Array[]>
  sendRawTransactions(transactions: Uint8Array[]): Promise<ConfirmedTxn & { id: string }>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAccountInfo(address: string): Promise<AccountInfo>
  getBalance(address: string): Promise<number>
  getAssets(address: string): Promise<Asset[]>
  waitForConfirmation(txId: string, timeout?: number): Promise<ConfirmedTxn>
}

abstract class BaseWallet implements BaseWalletInterface {
  protected algodClient: _algosdk.Algodv2
  protected algosdk: typeof _algosdk

  protected abstract id: PROVIDER_ID
  protected abstract provider: WalletProvider

  abstract connect(onDisconnect: () => void): Promise<Wallet>
  abstract disconnect(): Promise<void>
  abstract reconnect(onDisconnect: () => void): Promise<Wallet | null>
  abstract signTransactions(transaction: string[]): Promise<Uint8Array[]>
  abstract signEncodedTransactions(transactions: TransactionsArray): Promise<Uint8Array[]>

  protected constructor({ algosdk, token, server, port }: InitAlgodClient) {
    this.algodClient = new algosdk.Algodv2(token, server, port)
    this.algosdk = algosdk
  }

  async healthCheck() {
    return await this.algodClient.healthCheck().do()
  }

  async getAccountInfo(address: string) {
    const accountInfo = await this.algodClient.accountInformation(address).do()

    if (!accountInfo) {
      throw new Error('Unable to get account information')
    }

    return accountInfo as AccountInfo
  }

  async getBalance(address: string) {
    const accountInfo = await this.algodClient.accountInformation(address).do()

    if (!accountInfo || accountInfo.amount === undefined) {
      throw new Error('Unable to get account balance')
    }

    return accountInfo.amount as number
  }

  async getAssets(address: string) {
    const accountInfo = await this.algodClient.accountInformation(address).do()

    if (!accountInfo || accountInfo.assets === undefined) {
      throw new Error('Unable to get account assets')
    }

    return accountInfo.assets as Asset[]
  }

  async waitForConfirmation(txId: string, timeout = 4) {
    const status = await this.algodClient.status().do()

    if (status === undefined) {
      throw new Error('Unable to get node status')
    }

    const startround = status['last-round'] + 1
    let currentround = startround

    while (currentround < startround + timeout) {
      const pendingInfo = await this.algodClient.pendingTransactionInformation(txId).do()
      if (pendingInfo !== undefined) {
        if (pendingInfo['confirmed-round'] !== null && pendingInfo['confirmed-round'] > 0) {
          // Got the completed Transaction
          return pendingInfo as ConfirmedTxn
        } else {
          if (pendingInfo['pool-error'] != null && pendingInfo['pool-error'].length > 0) {
            // If there was a pool error, then the transaction has been rejected
            throw new Error(
              'Transaction ' + txId + ' rejected - pool error: ' + pendingInfo['pool-error']
            )
          }
        }
      }

      await this.algodClient.statusAfterBlock(currentround).do()
      currentround++
    }

    throw new Error('Transaction ' + txId + ' not confirmed after ' + timeout + ' rounds.')
  }

  decodeTransaction = (txn: string, isSigned: boolean) => {
    return isSigned
      ? this.algosdk.decodeSignedTransaction(new Uint8Array(Buffer.from(txn, 'base64'))).txn
      : this.algosdk.decodeUnsignedTransaction(new Uint8Array(Buffer.from(txn, 'base64')))
  }

  logEncodedTransaction(txn: string, isSigned: boolean) {
    const txnObj = this.decodeTransaction(txn, isSigned)

    console.log('TRANSACTION', {
      isSigned,
      from: txnObj.from && this.algosdk.encodeAddress(txnObj.from.publicKey),
      to: txnObj.to && this.algosdk.encodeAddress(txnObj.to.publicKey),
      type: txnObj.type,
      txn: txnObj
    })
  }

  groupTransactionsBySender(transactions: TransactionsArray) {
    function groupBy(objectArray: Record<string, any>[], property: string) {
      return objectArray.reduce(function (acc, obj) {
        const key = obj[property]
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(obj)
        return acc
      }, {})
    }

    const decodedGroup = transactions.reduce((acc: TxnInfo[], [type, txn], index) => {
      if (type === 'u') {
        const decodedTxn = this.decodeTransaction(txn, false)
        const from = decodedTxn.from ? this.algosdk.encodeAddress(decodedTxn.from.publicKey) : ''
        const to = decodedTxn.to ? this.algosdk.encodeAddress(decodedTxn.to.publicKey) : ''
        const type = (decodedTxn.type as TxnType) || ''
        const amount = Number(decodedTxn.amount) || 0 // convert from bigint to number

        const txnObj = {
          groupIndex: index,
          amount,
          from,
          to,
          type,
          txn
        }

        acc.push(txnObj)
      }

      return acc
    }, [])
    return groupBy(decodedGroup, 'from')
  }

  async sendRawTransactions(transactions: Uint8Array[]) {
    const sentTransaction = await this.algodClient.sendRawTransaction(transactions).do()

    if (!sentTransaction) {
      throw new Error('Transaction failed.')
    }

    const confirmedTransaction = await this.waitForConfirmation(sentTransaction.txId)

    return {
      id: sentTransaction.txId,
      ...confirmedTransaction
    }
  }
}

export default BaseWallet
